import { StandardEvents } from '@shopify/events';

/**
 * Fenêtre de confirmation « Produit ajouté au panier ».
 * Écoute l'événement standard `shopify:cart:lines-update` (action « add »), attend le
 * résultat de l'ajout, relit le panier puis ouvre un <dialog> modal avec le produit,
 * le nombre d'articles et les totaux. Markup : snippets/odl-cart-popup.liquid.
 */
class OdlCartPopup extends HTMLElement {
  /** @type {AbortController | null} */
  #abort = null;
  /** Jeton pour ignorer les réponses dépassées par un ajout plus récent. */
  #token = 0;

  /** @type {HTMLDialogElement | null} */
  get dialog() {
    return this.querySelector('dialog');
  }

  connectedCallback() {
    this.#abort?.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate, { signal });

    for (const button of this.querySelectorAll('[data-odl-cp-close]')) {
      button.addEventListener('click', () => this.close(), { signal });
    }

    // Clic sur le fond assombri : ferme la fenêtre.
    this.dialog?.addEventListener(
      'click',
      (event) => {
        if (event.target === this.dialog) this.close();
      },
      { signal }
    );
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  close() {
    if (this.dialog?.open) this.dialog.close();
  }

  /** @param {any} event */
  #onCartUpdate = async (event) => {
    if (event.action !== 'add') return;

    const dialog = this.dialog;
    if (!dialog) return;

    const token = ++this.#token;
    const lines = Array.isArray(event.lines) ? event.lines : [];
    const variantIds = lines.map((line) => String(line.merchandiseId).split('/').pop());
    const addedQuantity = lines.reduce((sum, line) => sum + (Number(line.quantity) || 0), 0);

    // Ajout depuis une autre fenêtre modale (ex. ajout rapide) : on attend sa fermeture.
    const sourceModal = event.target instanceof Element ? event.target.closest('dialog:modal') : null;
    if (sourceModal === dialog) return;

    try {
      const result = event.promise ? await event.promise : null;
      if (result && (result.userErrors?.length || result.detail?.didError || result.cart === null)) return;
    } catch (error) {
      return;
    }
    if (token !== this.#token) return;

    let cart;
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      cart = await response.json();
    } catch (error) {
      return;
    }
    if (token !== this.#token || !cart?.items?.length) return;

    // Le panier liste les lignes les plus récentes en premier.
    const item = cart.items.find((line) => variantIds.includes(String(line.variant_id))) || cart.items[0];
    this.#fill(item, cart, addedQuantity || item.quantity);

    const show = () => {
      if (!dialog.open) dialog.showModal();
      /** @type {HTMLElement | null} */ (this.querySelector('[data-odl-cp-checkout]'))?.focus();
    };

    if (sourceModal instanceof HTMLDialogElement && sourceModal.open) {
      sourceModal.addEventListener('close', show, { once: true });
    } else {
      show();
    }
  };

  /**
   * @param {any} item
   * @param {any} cart
   * @param {number} quantity
   */
  #fill(item, cart, quantity) {
    const money = new Intl.NumberFormat(document.documentElement.lang || 'fr-FR', {
      style: 'currency',
      currency: cart.currency || 'EUR',
    });
    /** @param {number} cents */
    const format = (cents) => money.format((Number(cents) || 0) / 100);
    /** @param {string} name */
    const field = (name) => /** @type {HTMLElement | null} */ (this.querySelector(`[data-odl-cp-${name}]`));
    /** @param {string} name @param {string} text */
    const setText = (name, text) => {
      const el = field(name);
      if (!el) return;
      el.textContent = text;
      el.hidden = !text;
    };

    const image = /** @type {HTMLImageElement | null} */ (field('img'));
    const media = this.querySelector('.odl-cp__media');
    if (image && media instanceof HTMLElement) {
      if (item.image) {
        const url = new URL(item.image, window.location.origin);
        url.searchParams.set('width', '600');
        image.src = url.toString();
        image.alt = item.product_title || '';
        media.hidden = false;
      } else {
        image.removeAttribute('src');
        media.hidden = true;
      }
    }

    const options = (item.options_with_values || [])
      .filter((option) => option.value && option.value !== 'Default Title')
      .map((option) => `${option.name} : ${option.value}`)
      .join(' · ');

    setText('vendor', item.vendor || '');
    setText('name', item.product_title || item.title || '');
    setText('variant', options);

    // Prix unitaire : prix catalogue, et mention de la remise si l'offre s'applique à cette ligne.
    const original = Number(item.original_price) || 0;
    const final = Number(item.final_price ?? original) || 0;
    let priceText = format(original);
    if (final < original) {
      priceText = final === 0 ? `Offerte (au lieu de ${format(original)})` : `${format(final)} au lieu de ${format(original)}`;
    }
    setText('price', priceText);
    setText('qty', String(quantity));

    const count = Number(cart.item_count) || 0;
    setText('count', `Il y a ${count} article${count > 1 ? 's' : ''} dans votre panier.`);

    // Sous-total avant remises, remise totale (lignes + panier), puis total à payer.
    const subtotal = Number(cart.original_total_price ?? cart.items_subtotal_price) || 0;
    const total = Number(cart.total_price) || 0;
    const discount = Math.max(0, subtotal - total);
    setText('subtotal', format(subtotal));
    setText('total', format(total));

    const discountRow = this.querySelector('[data-odl-cp-discount-row]');
    if (discountRow instanceof HTMLElement) {
      discountRow.hidden = discount <= 0;
      setText('discount', discount > 0 ? `− ${format(discount)}` : '');
    }
  }
}

if (!customElements.get('odl-cart-popup')) {
  customElements.define('odl-cart-popup', OdlCartPopup);
}
